import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { Observable, forkJoin } from "rxjs";
import { IAction } from "./action";
import { IContactMethod } from "./contact-method";
import { IContactType } from "./contact-type";
import { ICounty } from "./county";
import { IProgram } from "./program";
import { IReferredBy } from "./referred-by";
import { IReferredTo } from "./referred-to";
import { ISiteType } from "./site-type";
import { IState } from "./state";
import { IStatus } from "./status";
import { ITopic } from "./topic";
import { IUser } from "./user";
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})

export class BaseTableService {
  private apiUrl: string;
  authorized: boolean = false;
  lookupsLoaded: boolean = false;
  actions: IAction[];
  contactMethods: IContactMethod[];
  contactTypes: IContactType[];
  counties: ICounty[];
  programs: IProgram[];
  referredBy: IReferredBy[];
  referredTo: IReferredTo[];
  siteTypes: ISiteType[];
  states: IState[];
  statuses: IStatus[];
  topics: ITopic[];
  user: IUser;
  users: IUser[];
  userLookup: {};
  actionLookup: {};
  topicLookup: {};
  statusLookup: {};
  programLookup: {};
  contactTypeLookup: {};
  countyLookup: {};

  constructor(private readonly httpService?: HttpClient, private readonly msalService?: MsalService) {
    this.apiUrl = environment.privateApi + "calltracker/lookup/"
  }

  getLookups(): Observable<any[]> {
    let actions = this.getActions();
    let contactMethods = this.getContactMethods();
    let contactTypes = this.getContactTypes();
    let counties = this.getCounties();
    let programs = this.getPrograms();
    let referredBy = this.getReferredBy();
    let referredTo = this.getReferredTo();
    let siteTypes = this.getSiteTypes();
    let states = this.getStates();
    let statuses = this.getStatuses();
    let topics = this.getTopics();
    let users = this.getUsers();
    return forkJoin(
      [
        actions,
        contactMethods,
        contactTypes,
        counties,
        programs,
        referredBy,
        referredTo,
        siteTypes,
        states,
        statuses,
        topics,
        users
      ]
    );
  }

  setup() {
    this.getLookups().subscribe(
      lookups => {
        this.actions = lookups[0];
        this.contactMethods = lookups[1];
        this.contactTypes = lookups[2];
        this.counties = lookups[3];
        this.programs = lookups[4];
        this.referredBy = lookups[5];
        this.referredTo = lookups[6];
        this.siteTypes = lookups[7];
        this.states = lookups[8];
        this.statuses = lookups[9];
        this.topics = lookups[10];
        this.users = lookups[11];
      },
      error => console.log(error),
      () => this.prepareLookups()
    )

    this.user = this.getUser();    
    this.getLookups().subscribe(
      lookups => {
        this.actions = lookups[0];
        this.contactMethods = lookups[1];
        this.contactTypes = lookups[2];
        this.counties = lookups[3];
        this.programs = lookups[4];
        this.referredBy = lookups[5];
        this.referredTo = lookups[6];
        this.siteTypes = lookups[7];
        this.states = lookups[8];
        this.statuses = lookups[9];
        this.topics = lookups[10];
        this.users = lookups[11];
      },
      error => console.log(error),
      () => this.prepareLookups()
    );
  }

  getUser(): IUser {    
    const account = this.msalService?.instance.getActiveAccount();    
    var IUser: IUser = {
      userId: account?.username || '',
      firstName: account?.name?.split(' ')[1] || '',
      lastName: account?.name?.split(' ')[0] || ''
    };
    this.authorized = true;
    return IUser;    
  }

  getUsers(): Observable<IUser[]> {
    return this.httpService.get<IUser[]>(this.apiUrl + "users", { withCredentials: true });
  }

  getActions(): Observable<IAction[]> {
    return this.httpService.get<IAction[]>(this.apiUrl + "actions", { withCredentials: true });
  }

  getContactMethods(): Observable<IContactMethod[]> {
    return this.httpService.get<IContactMethod[]>(this.apiUrl + "contactMethods", { withCredentials: true });
  }

  getContactTypes(): Observable<IContactType[]> {
    return this.httpService.get<IContactType[]>(this.apiUrl + "contactTypes", { withCredentials: true });
  }

  getCounties(): Observable<ICounty[]> {
    return this.httpService.get<ICounty[]>(this.apiUrl + "counties", { withCredentials: true });
  }

  getPrograms(): Observable<IProgram[]> {
    return this.httpService.get<IProgram[]>(this.apiUrl + "programs", { withCredentials: true });
  }

  getReferredBy(): Observable<IReferredBy[]> {
    return this.httpService.get<IReferredBy[]>(this.apiUrl + "referredBy", { withCredentials: true });
  }

  getReferredTo(): Observable<IReferredTo[]> {
    return this.httpService.get<IReferredTo[]>(this.apiUrl + "referredTo", { withCredentials: true });
  }

  getSiteTypes(): Observable<ISiteType[]> {
    return this.httpService.get<ISiteType[]>(this.apiUrl + "siteTypes", { withCredentials: true });
  }

  getStates(): Observable<IState[]> {
    return this.httpService.get<IState[]>(this.apiUrl + "states", { withCredentials: true });
  }

  getStatuses(): Observable<IStatus[]> {
    return this.httpService.get<IStatus[]>(this.apiUrl + "statuses", { withCredentials: true });
  }

  getTopics(): Observable<ITopic[]> {
    return this.httpService.get<ITopic[]>(this.apiUrl + "topics", { withCredentials: true });
  }

  prepareLookups() {
    this.userLookup = [];
    this.actionLookup = [];
    this.topicLookup = [];
    this.statusLookup = [];
    this.programLookup = [];
    this.contactTypeLookup = [];
    this.countyLookup = [];
    this.users.forEach(user => {
      this.userLookup[user.userId] = user.firstName + ' ' + user.lastName;
    });
    this.actions.forEach(action => {
      this.actionLookup[action.actionId] = action.shortDescription;
    });
    this.topics.forEach(topic => {
      this.topicLookup[topic.topicId] = topic.shortDescription;
    });
    this.statuses.forEach(status => {
      this.statusLookup[status.statusId] = status.shortDescription;
    });
    this.programs.forEach(program => {
      this.programLookup[program.programId] = program.shortDescription;
    });
    this.contactTypes.forEach(contact => {
      this.contactTypeLookup[contact.contactTypeId] = contact.shortDescription;
    });
    this.counties.forEach(county => {
      this.countyLookup[county.countyId] = county.countyName;
    });
    this.lookupsLoaded = true;
  }
}
